import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import { Label } from '@/components/ui/label';
import {
  Building2, Globe, ExternalLink, ArrowLeft, Pencil, Save, X,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = '';

function getFaviconUrl(website) {
  if (!website) return null;
  try {
    const domain = website.replace(/^https?:\/\//, '').split('/')[0];
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`;
  } catch { return null; }
}

/**
 * Public Company page — intentionally minimal.
 * Shows ONLY the company's descriptor (name + description) and a "Visit website" CTA.
 * All rich content (collateral, news, team, etc.) lives on the Partner page
 * for companies marked as partners.
 */
export default function CompanyDetailPage({ user }) {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const canEdit = user?.role === 'admin';

  const startEdit = () => {
    setEditForm({
      description: company.description || '',
      website: company.website || '',
    });
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${companyId}`, {
        method: 'PUT', credentials: 'include', headers,
        body: JSON.stringify(editForm),
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Failed'); }
      const updated = await res.json();
      setCompany(prev => ({ ...prev, ...updated }));
      setEditing(false);
      toast.success('Company profile updated');
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/companies/${companyId}`, { credentials: 'include', headers })
      .then(async res => {
        if (res.ok) setCompany(await res.json());
        else { toast.error('Company not found'); navigate('/companies'); }
      })
      .catch(() => { toast.error('Failed to load'); navigate('/companies'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (loading) return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />
      <div className="flex justify-center pt-32"><div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" /></div>
    </div>
  );
  if (!company) return null;

  const websiteHref = company.website
    ? (company.website.startsWith('http') ? company.website : `https://${company.website}`)
    : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-3xl">
          <Button variant="ghost" size="sm" onClick={() => navigate('/companies')} className="mb-4" data-testid="back-to-companies">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Directory
          </Button>

          <Card className="overflow-hidden" data-testid="company-profile-card">
            <div className="h-3 bg-gradient-to-r from-brand-navy to-brand-sage" />
            <div className="p-4 sm:p-8">
              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white flex items-center justify-center flex-shrink-0 overflow-hidden border border-slate-200">
                  {company.logo_url ? (
                    <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-2" />
                  ) : getFaviconUrl(company.website) ? (
                    <img src={getFaviconUrl(company.website)} alt={company.name} className="w-10 h-10 sm:w-12 sm:h-12 object-contain"
                      onError={e => { e.target.style.display = 'none'; e.target.parentElement.querySelector('.fallback-icon').style.display = 'flex'; }} />
                  ) : null}
                  <div className="fallback-icon items-center justify-center" style={company.logo_url || getFaviconUrl(company.website) ? { display: 'none' } : { display: 'flex' }}>
                    <Building2 className="h-8 w-8 sm:h-10 sm:w-10 text-brand-navy/40" />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-3">
                    <h1 className="text-xl sm:text-3xl font-bold text-slate-800" data-testid="company-name">{company.name}</h1>
                    {canEdit && !editing && (
                      <Button variant="outline" size="sm" onClick={startEdit} className="ml-auto text-teal-600 hover:bg-teal-50" data-testid="edit-company-btn">
                        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
                      </Button>
                    )}
                  </div>

                  {!editing && (
                    <>
                      <div className="text-slate-600 leading-relaxed mb-5" data-testid="company-description">
                        {company.description ? <RichText html={company.description} /> : <span className="text-slate-400 italic">No description yet.{canEdit && ' Click Edit to add one.'}</span>}
                      </div>
                      {websiteHref && (
                        <a href={websiteHref} target="_blank" rel="noopener noreferrer" className="inline-block" data-testid="company-website-link">
                          <Button variant="outline" className="text-sm">
                            <Globe className="h-4 w-4 mr-2 text-brand-navy" /> Visit Website
                            <ExternalLink className="h-3 w-3 ml-2 text-slate-300" />
                          </Button>
                        </a>
                      )}
                    </>
                  )}

                  {editing && (
                    <div className="space-y-3" data-testid="company-edit-form">
                      <div>
                        <Label>Description</Label>
                        <RichTextEditor value={editForm.description}
                          onChange={v => setEditForm(p => ({ ...p, description: v }))}
                          placeholder="Describe the company..." testId="edit-company-description" minHeight="7rem" />
                      </div>
                      <div>
                        <Label>Website</Label>
                        <Input data-testid="edit-company-website" value={editForm.website}
                          onChange={e => setEditForm(p => ({ ...p, website: e.target.value }))}
                          placeholder="https://..." className="mt-1" />
                      </div>
                      <div className="flex gap-2 pt-2">
                        <Button onClick={handleSave} disabled={saving} className="bg-brand-navy hover:bg-brand-dark text-white gap-1.5" data-testid="save-company-profile-btn">
                          <Save className="h-4 w-4" /> {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button variant="outline" onClick={() => setEditing(false)}>
                          <X className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </div>
      </main>
    </div>
  );
}
